/**
 * Store IndexedDB pour la mémoire AI Engine (Phase 2, incrément 2.4).
 *
 * Contrairement aux blobs (photos / voice), la mémoire AI :
 *   - est petite (< 10 KB typique) — un seul objet `AiMemory` ;
 *   - est lue très souvent (chaque appel à `runAnalysis`) ;
 *   - est écrite occasionnellement (accept / dismiss / detectPatterns).
 *
 * Le module `modules/ai-engine/memory.ts` garde donc une API sync
 * (cache mémoire en RAM) et n'utilise ces helpers IDB qu'en backing
 * store async — hydratation au boot, persistance fire-and-forget à
 * chaque mutation.
 *
 * Object store : `ai-memory`, créé en 2.1, keyPath `'id'`. On y
 * stocke une **seule** entrée `{ id: 'main', data: <AiMemory>,
 * updatedAt }` — pas de versioning multi-entrée (l'historique de la
 * mémoire n'a aucun intérêt produit).
 */
import { openDb, type AiMemoryEntry } from './db.js';

/** Clé unique réservée pour la mémoire AI Engine. */
export const AI_MEMORY_KEY = 'main';

/**
 * Récupère l'entrée AI memory. Retourne `null` si rien n'est encore
 * persisté (premier boot post-migration), ou si IDB est indisponible.
 * Lecture défensive — `data` est typé `unknown` côté IDB, le caller
 * est responsable de valider la forme.
 */
export async function getAiMemoryEntry(): Promise<AiMemoryEntry | null> {
  const db = await openDb();
  if (!db) return null;
  try {
    return (await db.get('ai-memory', AI_MEMORY_KEY)) ?? null;
  } catch (err) {
    console.warn('[storage:aiMemory] get failed:', err);
    return null;
  }
}

/**
 * Persiste un payload `AiMemory` dans IDB sous la clé `'main'`.
 * Retourne `true` si l'écriture a abouti. L'appelant typique est
 * `saveMemory()` de `modules/ai-engine/memory.ts` qui le call en
 * fire-and-forget après la mutation du cache mémoire.
 */
export async function putAiMemory(data: unknown): Promise<boolean> {
  const db = await openDb();
  if (!db) return false;
  try {
    const entry: AiMemoryEntry = {
      id: AI_MEMORY_KEY,
      data,
      updatedAt: Date.now(),
    };
    await db.put('ai-memory', entry);
    return true;
  } catch (err) {
    console.warn('[storage:aiMemory] put failed:', err);
    return false;
  }
}

/**
 * Efface l'entrée AI memory. Utilisé exclusivement par
 * `_resetMemoryForTests()` pour repartir d'un état propre — pas
 * d'usage applicatif (la mémoire AI accumule la connaissance des
 * habitudes du foyer, on ne la purge jamais à la main).
 */
export async function deleteAiMemory(): Promise<boolean> {
  const db = await openDb();
  if (!db) return false;
  try {
    await db.delete('ai-memory', AI_MEMORY_KEY);
    return true;
  } catch (err) {
    console.warn('[storage:aiMemory] delete failed:', err);
    return false;
  }
}
